import { useState } from 'react';
import { Check, Download, X } from 'lucide-react';
import Button from './Button.js';
import DocumentModal from './DocumentModal.js';
import ReceiptExtractedFields from './ReceiptExtractedFields.js';
import SettlementAuditFields from './SettlementAuditFields.js';
import SettlementFieldList from './SettlementFieldList.js';
import SettlementModalShell from './SettlementModalShell.js';
import SettlementReceiptThumb from './SettlementReceiptThumb.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate, formatIstDateTime } from '../../utils/localDate.js';
import { statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const formatIsoTimestamps = (text) =>
  String(text || '').replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:[zZ]|[+-]\d{2}:?\d{2})?/g,
    (iso) => {
      const formatted = formatIstDateTime(iso);
      return formatted === '—' ? iso : formatted;
    },
  );

const friendlyNotes = (text) =>
  formatIsoTimestamps(
    String(text || '')
      .replace(/receipt OCR/gi, 'AI review')
      .replace(/\bOCR\b/g, 'AI'),
  );

const aiReviewLabel = (ocrStatus, requestStatus) => {
  if (requestStatus === 'OCR_PENDING') return 'AI verifying';
  const map = {
    PENDING: 'Queued',
    PROCESSING: 'AI verifying',
    PASSED: 'AI passed',
    FAILED: 'AI failed',
    ERROR: 'AI error',
  };
  return map[ocrStatus] || ocrStatus || '—';
};

const paymentModeLabel = (value) => {
  const raw = String(value || '').trim().replace(/_/g, ' ')
  return raw || '—'
}

const gcLabel = (ids) => {
  const list = ids || [];
  if (!list.length) return '—';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`;
};

const LineTable = ({ title, columns, rows }) => (
  <div className="hidden md:block bg-white rounded-xl border border-gray-100 overflow-hidden">
    <div className="px-3 py-2 border-b border-gray-100">
      <h3 className="text-xs font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[640px]">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={`px-2.5 py-2 font-semibold whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.key}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-2.5 py-2 ${col.align === 'right' ? 'text-right tabular-nums whitespace-nowrap' : 'whitespace-nowrap'} ${col.className || ''}`}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PaidLineCards = ({ rows }) => (
  <div className="md:hidden space-y-2">
    <h3 className="text-xs font-semibold text-gray-800">Paid remittance</h3>
    {rows.map((row) => (
      <div key={row.key} className="rounded-xl border border-gray-100 bg-white p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-sm break-all min-w-0">{row.ref}</p>
          <p className="tabular-nums font-semibold text-sm shrink-0">{fullCurrency(row.amount)}</p>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">{formatIndianDate(row.booking_date)} · {paymentModeLabel(row.mode_of_payment)}</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-600">
          <span>Paid <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.paid_amount)}</span></span>
          <span>Already remitted <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.previously_remitted)}</span></span>
          <span>Remaining <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.remaining_after)}</span></span>
          <span className="capitalize">{row.paid_remit_status || '—'}</span>
        </div>
      </div>
    ))}
  </div>
);

const ToPayLineCards = ({ rows }) => (
  <div className="md:hidden space-y-2">
    <h3 className="text-xs font-semibold text-gray-800">To-Pay remittance</h3>
    {rows.map((row) => (
      <div key={row.key} className="rounded-xl border border-gray-100 bg-white p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-sm break-all min-w-0">{row.ref}</p>
          <p className="tabular-nums font-semibold text-sm shrink-0">{fullCurrency(row.amount)}</p>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {formatIndianDate(row.created_at || row.booking_date)} · {row.location_name || '—'}
        </p>
        <p className="text-[11px] text-gray-600 mt-1 break-words">
          {row.gc_count || (row.shipment_ids || []).length || 0} GC(s) · {gcLabel(row.shipment_ids)}
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-600">
          <span>Freight <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.total_freight)}</span></span>
          <span>
            Commission{' '}
            <span className="tabular-nums font-medium text-gray-900">
              {row.commission_pct != null ? `${row.commission_pct}% · ` : ''}
              {fullCurrency(row.commission_amount)}
            </span>
          </span>
          <span>Net due <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_due)}</span></span>
          <span>Returned <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_returned)}</span></span>
        </div>
      </div>
    ))}
  </div>
);

const AmountCard = ({ label, value, tone }) => {
  const tones = {
    amber: 'border-amber-100 bg-amber-50/60',
    sky: 'border-sky-100 bg-sky-50/60',
    violet: 'border-violet-100 bg-violet-50/60',
    emerald: 'border-emerald-100 bg-emerald-50/60',
  };
  return (
    <div className={`rounded-xl border p-2 sm:p-2.5 min-w-0 ${tones[tone] || 'border-gray-100 bg-gray-50'}`}>
      <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight">{label}</p>
      <p className="font-semibold tabular-nums text-xs sm:text-base leading-tight">{fullCurrency(value)}</p>
    </div>
  );
};

const pickDue = (value, fallback) => (value != null && value !== '' ? value : fallback);

const mergeToPayLine = (line, dueHints) => {
  const collapse = (value) => String(value || '').replace(/\s+/g, '');
  const due = (dueHints || []).find((item) => (
    item.ml_sheet_id === line.ref || collapse(item.ml_sheet_id) === collapse(line.ref)
  )) || {};
  return {
    ...line,
    created_at: pickDue(line.created_at, due.created_at),
    location_name: pickDue(line.location_name, due.location_name),
    total_freight: pickDue(line.total_freight, due.total_freight),
    commission_pct: pickDue(line.commission_pct, due.commission_pct),
    commission_amount: pickDue(line.commission_amount, due.commission_amount),
    amount_due: pickDue(line.amount_due, due.amount_due),
    amount_returned: pickDue(line.amount_returned, due.amount_returned),
    shipment_ids: (line.shipment_ids && line.shipment_ids.length) ? line.shipment_ids : (due.shipment_ids || []),
    gc_count: line.gc_count || due.shipment_count || (due.shipment_ids || []).length,
  };
};

export default function CompanySettlementBatchView({
  batch,
  onClose,
  dueHints = [],
  canReview = false,
  busy = false,
  onApprove,
  onReject,
}) {
  const [receiptOpen, setReceiptOpen] = useState(false);
  if (!batch) return null;

  const primary = (batch.paid_requests || []).concat(batch.to_pay_requests || [])[0] || {};
  const ocr = primary.ocr_result_json || {};
  const checks = (ocr.checks || []).filter((c) => c.name !== 'extracted_payment_fields');
  const failedChecks = checks.filter((c) => !c.passed);
  const passedChecks = checks.filter((c) => c.passed);
  const receiptUrl = batch.receipt_url || primary.receipt_url;
  const excelUrl = primary.shipments_excel_url;
  const paidLines = batch.paid_lines || [];
  const toPayLines = (batch.to_pay_lines || []).map((line) => mergeToPayLine(line, dueHints));
  const showPaid = paidLines.length > 0 || Number(batch.paid_amount || 0) > 0;
  const showToPay = toPayLines.length > 0 || Number(batch.to_pay_amount || 0) > 0;
  const commissionAmount = Number(batch.commission_amount || 0) || toPayLines.reduce(
    (sum, line) => sum + Number(line.commission_amount || 0),
    0,
  );
  const requestForAudit = {
    ...primary,
    submitted_at: batch.submitted_at || primary.submitted_at,
    reviewed_at: batch.reviewed_at || primary.reviewed_at,
    reviewed_by: batch.reviewed_by || primary.reviewed_by,
    reviewed_by_name: batch.reviewed_by_name || primary.reviewed_by_name,
    reviewed_by_display: batch.reviewed_by_display || primary.reviewed_by_display,
    status: batch.status || primary.status,
  };

  return (
    <>
      <SettlementModalShell
        title="Settlement request"
        subtitle={batch.batch_id}
        onClose={onClose}
        size="xl"
        headerActions={canReview ? (
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              icon={X}
              mobileIconOnly
              disabled={busy}
              onClick={() => onReject?.(batch.batch_id)}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={Check}
              mobileIconOnly
              disabled={busy}
              onClick={() => onApprove?.(batch.batch_id)}
            >
              Approve
            </Button>
          </div>
        ) : null}
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start text-sm">
            <div className="min-w-0 flex flex-col gap-2">
                <div className={`grid gap-1.5 sm:gap-2 ${showPaid && showToPay ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  {showPaid ? (
                    <AmountCard label="Paid" value={batch.paid_amount} tone="amber" />
                  ) : null}
                  {showToPay ? (
                    <AmountCard label="To-Pay" value={batch.to_pay_amount} tone="sky" />
                  ) : null}
                  <AmountCard label="Commission" value={commissionAmount} tone="violet" />
                  <AmountCard label="Total" value={batch.total_amount} tone="emerald" />
                </div>

                <SettlementFieldList
                  items={[
                    {
                      label: 'Status',
                      value: (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${statusBadge(batch.status)}`}>
                          {statusLabel(batch.status)}
                        </span>
                      ),
                    },
                    {
                      label: 'AI review',
                      value: aiReviewLabel(primary.ocr_status, batch.status),
                    },
                    {
                      label: 'Mode',
                      value: `${batch.payment_mode || '—'}${batch.payment_ref ? ` · ${batch.payment_ref}` : ''}`,
                      span: 2,
                    },
                    {
                      label: 'Settle to',
                      value: (
                        batch.destination_account_label
                        || ({
                          head_office: 'Head Office',
                          company_account: 'Company account',
                          branch: 'Admin branch',
                        }[batch.destination_type] || 'Admin branch')
                      ),
                      span: 2,
                    },
                    {
                      label: 'Whose amount',
                      value: batch.agent_name || batch.agent_user_id || '—',
                    },
                    {
                      label: 'Created by',
                      value: batch.submitted_by_name || batch.submitted_by || '—',
                    },
                  ]}
                />

                <SettlementAuditFields request={requestForAudit} />

                {(primary.ocr_notes || primary.review_notes || batch.remarks) && (
                  <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-2.5 py-1.5">
                    <div className="text-xs font-semibold text-sky-900 mb-1">Summary</div>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap break-words">
                      {friendlyNotes(primary.ocr_notes || primary.review_notes || batch.remarks)}
                    </p>
                  </div>
                )}

                {receiptUrl ? (
                  <SettlementReceiptThumb url={receiptUrl} onView={() => setReceiptOpen(true)} showDownload />
                ) : null}

              {excelUrl ? (
                <div className="flex flex-wrap gap-2 pt-0.5">
                  <a
                    href={excelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download Excel
                  </a>
                </div>
              ) : null}
            </div>

            <div className="min-w-0 flex flex-col rounded-xl border border-gray-200 border-l-4 border-l-orange-400 bg-white p-3 space-y-2">
                <div className="text-xs font-semibold text-gray-800">
                  AI review — {aiReviewLabel(primary.ocr_status, batch.status)}
                </div>
                  {failedChecks.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-red-800 mb-1">
                        Failed checks ({failedChecks.length})
                      </div>
                      <ul className="space-y-1">
                        {failedChecks.map((check) => (
                          <li key={check.name} className="text-[11px] text-red-800 flex gap-1.5">
                            <span className="shrink-0 font-semibold">FAIL</span>
                            <span className="break-words">{friendlyNotes(check.detail)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {passedChecks.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold text-green-800 mb-1">
                        Passed checks ({passedChecks.length})
                      </div>
                      <ul className="space-y-1">
                        {passedChecks.map((check) => (
                          <li key={check.name} className="text-[11px] text-green-700 flex gap-1.5">
                            <span className="shrink-0 font-semibold">PASS</span>
                            <span className="break-words">{friendlyNotes(check.detail)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <ReceiptExtractedFields ocrResult={ocr} />
                  {checks.length === 0 && (
                    <p className="text-xs text-orange-800">No AI checks yet.</p>
                  )}
            </div>
          </div>

          <div className="flex flex-col gap-3">
          {paidLines.length > 0 ? (
            <>
              <PaidLineCards rows={paidLines.map((line) => ({ ...line, key: `p-${line.ref}` }))} />
              <LineTable
              title="Paid remittance"
              rows={paidLines.map((line) => ({ ...line, key: `p-${line.ref}` }))}
              columns={[
                { key: 'ref', label: 'GC', render: (row) => row.ref },
                { key: 'date', label: 'Date', render: (row) => formatIndianDate(row.booking_date) },
                {
                  key: 'paid',
                  label: 'Paid',
                  align: 'right',
                  render: (row) => fullCurrency(row.paid_amount),
                },
                {
                  key: 'prev',
                  label: 'Already remitted',
                  align: 'right',
                  render: (row) => fullCurrency(row.previously_remitted),
                },
                {
                  key: 'amount',
                  label: 'This remittance',
                  align: 'right',
                  render: (row) => fullCurrency(row.amount),
                },
                {
                  key: 'remain',
                  label: 'Remaining',
                  align: 'right',
                  render: (row) => fullCurrency(row.remaining_after),
                },
                { key: 'mode', label: 'Mode', render: (row) => paymentModeLabel(row.mode_of_payment), className: 'capitalize' },
                {
                  key: 'status',
                  label: 'Status',
                  render: (row) => row.paid_remit_status || '—',
                  className: 'capitalize',
                },
              ]}
            />
            </>
          ) : null}

          {toPayLines.length > 0 ? (
            <>
              <ToPayLineCards rows={toPayLines.map((line) => ({ ...line, key: `t-${line.ref}` }))} />
              <LineTable
              title="To-Pay remittance"
              rows={toPayLines.map((line) => ({ ...line, key: `t-${line.ref}` }))}
              columns={[
                { key: 'ref', label: 'ML Sheet', render: (row) => row.ref },
                {
                  key: 'date',
                  label: 'Date',
                  render: (row) => formatIndianDate(row.created_at || row.booking_date),
                },
                { key: 'loc', label: 'Location', render: (row) => row.location_name || '—' },
                {
                  key: 'gcs',
                  label: 'GCs',
                  render: (row) => `${row.gc_count || (row.shipment_ids || []).length || 0} · ${gcLabel(row.shipment_ids)}`,
                  className: 'text-gray-600 max-w-[200px] truncate',
                },
                {
                  key: 'freight',
                  label: 'Freight',
                  align: 'right',
                  render: (row) => fullCurrency(row.total_freight),
                },
                {
                  key: 'comm',
                  label: 'Commission',
                  align: 'right',
                  render: (row) => (
                    <span>
                      {row.commission_pct != null ? `${row.commission_pct}% · ` : ''}
                      {fullCurrency(row.commission_amount)}
                    </span>
                  ),
                },
                {
                  key: 'due',
                  label: 'Net due',
                  align: 'right',
                  render: (row) => fullCurrency(row.amount_due),
                },
                {
                  key: 'returned',
                  label: 'Returned',
                  align: 'right',
                  render: (row) => fullCurrency(row.amount_returned),
                },
                {
                  key: 'amount',
                  label: 'This remittance',
                  align: 'right',
                  render: (row) => fullCurrency(row.amount),
                },
              ]}
            />
            </>
          ) : null}
          </div>
        </div>
      </SettlementModalShell>

      {receiptOpen && receiptUrl ? (
        <DocumentModal
          isOpen={receiptOpen}
          onClose={() => setReceiptOpen(false)}
          documentUrl={receiptUrl}
          documentType="image"
          title="Payment receipt"
          zIndexClass="z-[90]"
        />
      ) : null}
    </>
  );
}
